import DatePicker from '@/components/date-picker';
import { PlanService } from '@/services/Plan.service';
import { ChevronLeft } from '@mui/icons-material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useToggle } from '@vestwell-frontend/hooks';

import { useFormik } from 'formik';
import { lowerCase, snakeCase, startCase, toUpper } from 'lodash';
import { useCallback } from 'react';
import { NumericFormat } from 'react-number-format';
import { useParams, useSearchParams } from 'react-router-dom';

const STATUSES = {
  ALL: 'All',
  AWAITING_FUNDS: 'Awaiting Funds',
  COMPLETED: 'Completed',
  DUE: 'Due',
  FUNDING_CONFIRMED: 'Funding Confirmed',
  INVESTMENT_PENDING: 'Investment Pending',
  LATE: 'Late',
  LATE_CRITICAL: 'Late Critical',
  MISSING: 'Missing',
  PROCESSING: 'Processing',
  SUBMISSION_STARTED: 'Submission Started',
  UPCOMING: 'Upcoming'
};

const FLOW_SUBTYPES = {
  ALL: 'All',
  CORRECTION: 'Correction',
  LOAN_PAYMENT: 'Loan Payment',
  LOST_GAINS: 'Lost Gain',
  MATCH_REMEDIATION: 'Match Remediation',
  'OFF-CYCLE_SUPPLEMENTAL_PAY': 'Off Cycle',
  REGULAR: 'Regular'
};

const PAYROLL_DUE_STATUSES = [
  'MISSING',
  'DUE',
  'UPCOMING',
  'LATE',
  'LATE_CRITICAL'
];

export const ContributionsFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isPannel, togglePanel] = useToggle(true);
  const params = useParams<{ sponsorPlanId: string }>();

  const payrollSetups = useQuery(
    [PlanService.getPayrollSetups.name, params.sponsorPlanId],
    () => PlanService.getPayrollSetups(params.sponsorPlanId),
    {
      enabled: !!params.sponsorPlanId,
      select: data => {
        return data.reduce(
          (acc, current) => ({
            ...(current.isActive
              ? {
                  ...acc,
                  activePayrollSetups: [...acc.activePayrollSetups, current]
                }
              : {
                  ...acc,
                  inactivePayrollSetups: [...acc.inactivePayrollSetups, current]
                })
          }),
          {
            activePayrollSetups: [],
            inactivePayrollSetups: []
          }
        );
      }
    }
  );

  const formik = useFormik({
    enableReinitialize: false,
    initialValues: {
      flowSubtypes: searchParams
        .get('flowSubtypes')
        ?.split(',')
        ?.map(flowSubtype => flowSubtype.toUpperCase()) ?? ['ALL'],
      maxPayDate: searchParams.get('maxPayDate') ?? '',
      maxTotal: searchParams.get('maxTotal') ?? '',
      minPayDate: searchParams.get('minPayDate') ?? '',
      minTotal: searchParams.get('minTotal') ?? '',
      payGroups: searchParams.get('payGroups')?.split(',') ?? ['ALL'],
      statuses:
        searchParams.has('statuses') || searchParams.has('payrollDueStatuses')
          ? [
              ...(searchParams.get('statuses')?.split(',') ?? []),
              ...(searchParams
                .get('payrollDueStatuses')
                ?.split(',')
                ?.map(value => toUpper(snakeCase(value))) ?? [])
            ]
          : ['ALL'],
      ucids: searchParams.get('ucids') ?? ''
    },
    onSubmit: values => {
      const payrollDueStatuses =
        values.statuses
          ?.filter(value => PAYROLL_DUE_STATUSES.includes(value))
          ?.map(value => startCase(lowerCase(value))) ?? [];

      const statuses =
        values.statuses?.filter(
          value => !PAYROLL_DUE_STATUSES.includes(value)
        ) ?? [];

      setSearchParams({
        ...(payrollDueStatuses.length
          ? { payrollDueStatuses: payrollDueStatuses.join(',') }
          : {}),
        ...(statuses.length && !values.statuses?.includes('ALL')
          ? { statuses: statuses.join(',') }
          : {}),
        ...(values.minTotal ? { minTotal: values.minTotal } : {}),
        ...(values.maxPayDate ? { maxPayDate: values.maxPayDate } : {}),
        ...(values.payGroups &&
        values.payGroups?.length &&
        !values.payGroups?.includes('ALL')
          ? { payGroups: values.payGroups?.join(',') }
          : {}),
        ...(values.minPayDate ? { minPayDate: values.minPayDate } : {}),
        ...(values.maxTotal ? { maxTotal: values.maxTotal } : {}),
        ...(values.flowSubtypes &&
        values.flowSubtypes?.length &&
        !values.flowSubtypes?.includes('ALL')
          ? {
              flowSubtypes: values.flowSubtypes
                ?.map(flowSubtype => flowSubtype.toLocaleLowerCase())
                ?.join(',')
            }
          : {}),
        ...(values.ucids ? { ucids: values.ucids?.replace(/\s/g, '') } : {})
      });
    }
  });

  const onReset = useCallback(() => {
    formik.resetForm({
      values: {
        flowSubtypes: ['ALL'],
        maxPayDate: '',
        maxTotal: '',
        minPayDate: '',
        minTotal: '',
        payGroups: ['ALL'],
        statuses: ['ALL'],
        ucids: ''
      }
    });

    setSearchParams({});
  }, []);

  const onSubmit = useCallback(() => formik.submitForm(), []);

  const onDropdownValueChanged = useCallback(event => {
    if (event.target?.value?.[event.target?.value?.length - 1] === 'ALL') {
      return formik.setFieldValue(event.target?.name, ['ALL']);
    }

    if (
      event.target?.value?.includes('ALL') &&
      event.target?.value?.[event.target?.value?.length - 1] !== 'ALL'
    ) {
      return formik.setFieldValue(
        event.target?.name,
        event.target?.value?.filter(value => value !== 'ALL')
      );
    }

    return formik.setFieldValue(event.target?.name, event.target?.value);
  }, []);

  return (
    <Stack gap={2} p={2}>
      <Stack alignItems='center' flexDirection='row'>
        {isPannel ? (
          <Stack
            display='flex'
            flex={1}
            flexDirection='row'
            justifyContent='space-between'>
            <Stack alignItems='center' flexDirection='row'>
              <IconButton>
                <FilterListIcon onClick={togglePanel} />
              </IconButton>
              <Typography variant='h6'>Filters</Typography>
            </Stack>
            <Stack>
              <IconButton onClick={togglePanel}>
                <ChevronLeft />
              </IconButton>
            </Stack>
          </Stack>
        ) : (
          <IconButton onClick={togglePanel}>
            <FilterListIcon />
          </IconButton>
        )}
      </Stack>
      <Collapse in={isPannel} orientation='horizontal'>
        <Stack gap={2} maxWidth={280}>
          <Stack>
            <Stack gap={2}>
              <FormControl fullWidth size='small'>
                <TextField
                  InputProps={{
                    endAdornment: <SearchIcon />
                  }}
                  data-testid='ucids'
                  helperText='Enter one or more UCIDs, separated by comma'
                  label='Search by UCIDs'
                  name='ucids'
                  onChange={formik.handleChange}
                  size='small'
                  value={formik.values?.ucids}
                  variant='outlined'
                />
              </FormControl>

              <FormControl fullWidth size='small'>
                <InputLabel shrink>Status</InputLabel>
                <Select
                  data-testid='statuses'
                  displayEmpty
                  input={<OutlinedInput label='Status' notched />}
                  label='Status'
                  multiple
                  name='statuses'
                  onChange={onDropdownValueChanged}
                  renderValue={selected => (
                    <Stack
                      display='flex'
                      flexDirection='row'
                      flexWrap='wrap'
                      gap={1}>
                      {selected.map(value => (
                        <Chip
                          key={value}
                          label={STATUSES[value] || value}
                          size='small'
                          variant='outlined'
                        />
                      ))}
                    </Stack>
                  )}
                  value={formik.values?.statuses}>
                  <MenuItem value='ALL'>
                    <Checkbox
                      checked={formik.values.statuses?.includes('ALL')}
                    />
                    <ListItemText>All</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem value='SUBMISSION_STARTED'>
                    <Checkbox
                      checked={formik.values.statuses?.includes(
                        'SUBMISSION_STARTED'
                      )}
                    />
                    <ListItemText>Submission Started</ListItemText>
                  </MenuItem>
                  <MenuItem value='PROCESSING'>
                    <Checkbox
                      checked={formik.values.statuses?.includes('PROCESSING')}
                    />
                    <ListItemText>Processing</ListItemText>
                  </MenuItem>
                  <MenuItem value='AWAITING_FUNDS'>
                    <Checkbox
                      checked={formik.values.statuses?.includes(
                        'AWAITING_FUNDS'
                      )}
                    />
                    <ListItemText>Awaiting Funds</ListItemText>
                  </MenuItem>
                  <MenuItem value='FUNDING_CONFIRMED'>
                    <Checkbox
                      checked={formik.values.statuses?.includes(
                        'FUNDING_CONFIRMED'
                      )}
                    />
                    <ListItemText>Funding Confirmed</ListItemText>
                  </MenuItem>
                  <MenuItem value='INVESTMENT_PENDING'>
                    <Checkbox
                      checked={formik.values.statuses?.includes(
                        'INVESTMENT_PENDING'
                      )}
                    />
                    <ListItemText>Investment Pending</ListItemText>
                  </MenuItem>
                  <MenuItem value='COMPLETED'>
                    <Checkbox
                      checked={formik.values.statuses?.includes('COMPLETED')}
                    />
                    <ListItemText>Completed</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem value='UPCOMING'>
                    <Checkbox
                      checked={formik.values.statuses?.includes('UPCOMING')}
                    />
                    <ListItemText>Upcoming</ListItemText>
                  </MenuItem>
                  <MenuItem value='DUE'>
                    <Checkbox
                      checked={formik.values.statuses?.includes('DUE')}
                    />
                    <ListItemText>Due</ListItemText>
                  </MenuItem>
                  <MenuItem value='LATE'>
                    <Checkbox
                      checked={formik.values.statuses?.includes('LATE')}
                    />
                    <ListItemText>Late</ListItemText>
                  </MenuItem>
                  <MenuItem value='LATE_CRITICAL'>
                    <Checkbox
                      checked={formik.values.statuses?.includes(
                        'LATE_CRITICAL'
                      )}
                    />
                    <ListItemText>Late Critical</ListItemText>
                  </MenuItem>
                  <MenuItem value='MISSING'>
                    <Checkbox
                      checked={formik.values.statuses?.includes('MISSING')}
                    />
                    <ListItemText>Missing</ListItemText>
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size='small'>
                <InputLabel shrink>Contribution Type</InputLabel>
                <Select
                  data-testid='flowSubtypes'
                  displayEmpty
                  input={<OutlinedInput label='Contribution Type' notched />}
                  label='Contribution Type'
                  multiple
                  name='flowSubtypes'
                  onChange={onDropdownValueChanged}
                  renderValue={selected => (
                    <Stack
                      display='flex'
                      flexDirection='row'
                      flexWrap='wrap'
                      gap={1}>
                      {selected.map(value => (
                        <Chip
                          key={value}
                          label={FLOW_SUBTYPES[value] || value}
                          size='small'
                          variant='outlined'
                        />
                      ))}
                    </Stack>
                  )}
                  value={formik.values?.flowSubtypes}>
                  <MenuItem value='ALL'>
                    <Checkbox
                      checked={formik.values.flowSubtypes?.includes('ALL')}
                    />
                    <ListItemText>All</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem value='REGULAR'>
                    <Checkbox
                      checked={formik.values.flowSubtypes?.includes('REGULAR')}
                    />
                    <ListItemText>Regular</ListItemText>
                  </MenuItem>
                  <MenuItem value='OFF-CYCLE_SUPPLEMENTAL_PAY'>
                    <Checkbox
                      checked={formik.values.flowSubtypes?.includes(
                        'OFF-CYCLE_SUPPLEMENTAL_PAY'
                      )}
                    />
                    <ListItemText>Off Cycle</ListItemText>
                  </MenuItem>
                  <MenuItem value='LOST_GAINS'>
                    <Checkbox
                      checked={formik.values.flowSubtypes?.includes(
                        'LOST_GAINS'
                      )}
                    />
                    <ListItemText>Lost Gain</ListItemText>
                  </MenuItem>
                  <MenuItem value='CORRECTION'>
                    <Checkbox
                      checked={formik.values.flowSubtypes?.includes(
                        'CORRECTION'
                      )}
                    />
                    <ListItemText>Correction</ListItemText>
                  </MenuItem>
                  <MenuItem value='LOAN_PAYMENT'>
                    <Checkbox
                      checked={formik.values.flowSubtypes?.includes(
                        'LOAN_PAYMENT'
                      )}
                    />
                    <ListItemText>Loan Payment</ListItemText>
                  </MenuItem>
                  <MenuItem value='MATCH_REMEDIATION'>
                    <Checkbox
                      checked={formik.values.flowSubtypes?.includes(
                        'MATCH_REMEDIATION'
                      )}
                    />
                    <ListItemText>Match Remediation</ListItemText>
                  </MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size='small'>
                <InputLabel shrink>Pay Group</InputLabel>
                <Select
                  data-testid='payGroups'
                  displayEmpty
                  input={<OutlinedInput label='Pay Group' notched />}
                  label='Pay Group'
                  multiple
                  name='payGroups'
                  onChange={onDropdownValueChanged}
                  renderValue={selected => {
                    return (
                      <Stack
                        display='flex'
                        flexDirection='row'
                        flexWrap='wrap'
                        gap={1}>
                        {selected.map(value => (
                          <Chip
                            key={value}
                            label={value === 'ALL' ? 'All' : value}
                            size='small'
                            variant='outlined'
                          />
                        ))}
                      </Stack>
                    );
                  }}
                  value={formik.values?.payGroups}>
                  <MenuItem value='ALL'>
                    <Checkbox
                      checked={formik.values.payGroups?.includes('ALL')}
                    />
                    <ListItemText>All</ListItemText>
                  </MenuItem>
                  <Divider />
                  {payrollSetups.data?.activePayrollSetups.map(payrollSetup => (
                    <MenuItem
                      key={payrollSetup.payGroupName}
                      value={payrollSetup.payGroupName}>
                      <Checkbox
                        checked={formik.values.payGroups?.includes(
                          payrollSetup.payGroupName
                        )}
                      />
                      <ListItemText>
                        <Typography>{payrollSetup.payGroupName}</Typography>
                        <Typography color='gray' variant='caption'>
                          ID: {payrollSetup.division ?? 'Null'}
                        </Typography>
                      </ListItemText>
                    </MenuItem>
                  ))}
                  {payrollSetups.data?.inactivePayrollSetups.length && (
                    <Divider />
                  )}
                  {payrollSetups.data?.inactivePayrollSetups.map(
                    payrollSetup => (
                      <MenuItem
                        key={payrollSetup.payGroupName}
                        value={payrollSetup.payGroupName}>
                        <Checkbox
                          checked={formik.values.payGroups?.includes(
                            payrollSetup.payGroupName
                          )}
                        />
                        <ListItemText>
                          <Typography>{payrollSetup.payGroupName}</Typography>
                          <Typography color='gray' variant='caption'>
                            ID: {payrollSetup.division ?? 'Null'} | Inactive
                          </Typography>
                        </ListItemText>
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>

              <Divider textAlign='left'>
                <Typography color='gray' variant='subtitle2'>
                  Total Contribution Amount
                </Typography>
              </Divider>

              <Stack display='flex' flexDirection='row' gap={2}>
                <FormControl fullWidth size='small'>
                  <NumericFormat
                    allowNegative={false}
                    customInput={TextField}
                    data-testid='from'
                    defaultValue={0}
                    label='From'
                    name='minTotal'
                    onValueChange={values =>
                      formik.setFieldValue('minTotal', values.floatValue)
                    }
                    prefix='$'
                    size='small'
                    thousandSeparator
                    value={formik.values?.minTotal}
                    valueIsNumericString
                    variant='outlined'
                  />
                </FormControl>

                <FormControl fullWidth size='small'>
                  <NumericFormat
                    allowNegative={false}
                    customInput={TextField}
                    data-testid='to'
                    defaultValue={0}
                    label='To'
                    name='maxTotal'
                    onValueChange={values =>
                      formik.setFieldValue('maxTotal', values.floatValue)
                    }
                    prefix='$'
                    size='small'
                    thousandSeparator
                    value={formik.values?.maxTotal}
                    valueIsNumericString
                    variant='outlined'
                  />
                </FormControl>
              </Stack>

              <Divider textAlign='left'>
                <Typography color='gray' variant='subtitle2'>
                  Pay Date
                </Typography>
              </Divider>

              <FormControl fullWidth>
                <DatePicker
                  InputProps={{ autoComplete: 'off' }}
                  data-testid='minPayDate'
                  label='Start Date'
                  name='minPayDate'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  size='small'
                  value={formik.values.minPayDate}
                  variant='outlined'
                />
              </FormControl>

              <FormControl fullWidth>
                <DatePicker
                  InputProps={{ autoComplete: 'off' }}
                  data-testid='maxPayDate'
                  label='End Date'
                  name='maxPayDate'
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  size='small'
                  value={formik.values.maxPayDate}
                  variant='outlined'
                />
              </FormControl>
            </Stack>
          </Stack>
          <Stack display='flex' flexDirection='row' gap={2}>
            <Button data-testid='apply' onClick={onSubmit} variant='outlined'>
              Apply
            </Button>
            <Button data-testid='reset' onClick={onReset} variant='outlined'>
              Reset
            </Button>
          </Stack>
        </Stack>
      </Collapse>
    </Stack>
  );
};
